import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { FieldLabel } from '../../components/ui/FieldLabel';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { HoverTooltip } from '../../components/ui/HoverTooltip';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { contactFieldHelp } from './contactFieldHelp';
import { listCompanies, Company } from '../../services/coreCompaniesApi';
import { CommunicationRole, listCommunicationRoles } from '../../services/communicationRolesApi';
import {
  Contact,
  ContactUpsertPayload,
  createContact,
  deleteContact,
  listContacts,
  updateContact,
} from '../../services/coreContactsApi';

const PAGE_SIZE = 10;

const roleTooltips: Record<string, string> = {
  Account: 'Conta Recebe a mensagem de boas-vindas e as pesquisas de satisfação CSAT e NPS.',
  Project: 'Projeto Recebe atualizações sobre o andamento do projeto, incluindo etapas, prazos, pendências e entregas.',
  InformationTechnology:
    'TI Recebe comunicações sobre configuração técnica, integrações, acessos e manutenções da solução.',
  Finance: 'Financeiro Recebe comunicações sobre faturamento, notas fiscais, cobranças e vencimentos.',
  Support:
    'Suporte técnico Recebe comunicações sobre chamados de suporte, incidentes e resolução de problemas, incluindo atualizações e orientações técnicas.',
};

const emptyForm: ContactUpsertPayload = {
  companyId: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  jobTitle: '',
  isPrimary: false,
  isActive: true,
  communicationRoleIds: [],
};

export function ContactsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('core.contacts');
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [roles, setRoles] = useState<CommunicationRole[]>([]);
  const [items, setItems] = useState<Contact[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [companyFilter, setCompanyFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ContactUpsertPayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    void (async () => {
      try {
        const [companyResult, roleResult] = await Promise.all([
          listCompanies({ activeOnly: true, page: 1, pageSize: 200 }),
          listCommunicationRoles(),
        ]);
        setCompanies(companyResult.items);
        setRoles(roleResult);
      } catch {
        toast.error('Falha ao carregar empresas.');
      }
    })();
  }, []);

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listContacts({
        page: targetPage,
        pageSize: PAGE_SIZE,
        companyId: companyFilter || undefined,
        search: search || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar contatos.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(contact: Contact): void {
    expandForm();
    setEditingId(contact.id);
    setForm({
      companyId: contact.companyId,
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phone: contact.phone ?? '',
      jobTitle: contact.jobTitle ?? '',
      isPrimary: contact.isPrimary,
      isActive: contact.isActive,
      communicationRoleIds: contact.communicationRoleIds ?? [],
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    setSaving(true);
    try {
      if (editingId) {
        await updateContact(editingId, form);
        toast.success('Contato atualizado.');
        collapseForm(resetForm);
      } else {
        await createContact(form);
        toast.success('Contato criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao salvar contato.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(contact: Contact): Promise<void> {
    if (
      !canWrite
      || !(await confirm({
        title: 'Excluir contato',
        message: `Excluir "${contact.fullName}"?`,
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }

    try {
      await deleteContact(contact.id);
      toast.success('Contato excluído.');
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir contato.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Contatos"
        description="Pessoas vinculadas às empresas. O papel de cada contato define quais comunicações ela recebe."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar contato' : 'Novo contato'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId
              ? `Editando ${form.firstName || 'contato'} — o formulário abriu em uma janela`
              : undefined
          }
          createSummary="Clique para cadastrar um novo contato"
          isEditing={!!editingId}
          modalSubtitle="Uma pessoa pode acumular papéis. Passe o mouse sobre cada item para ver quais comunicações ela recebe."
        >
        <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                <FieldLabel {...contactFieldHelp.firstName} required>
                  Nome
                </FieldLabel>
                <input
                  className="erp-input"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                <FieldLabel {...contactFieldHelp.lastName}>Sobrenome</FieldLabel>
                <input
                  className="erp-input"
                  value={form.lastName ?? ''}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                />
              </label>
              <label className="erp-label">
                <FieldLabel {...contactFieldHelp.email} required>
                  E-mail
                </FieldLabel>
                <input
                  type="email"
                  className="erp-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                <FieldLabel {...contactFieldHelp.company} required>
                  Empresa
                </FieldLabel>
                <select
                  className="erp-input"
                  value={form.companyId}
                  onChange={(e) => setForm({ ...form, companyId: e.target.value })}
                  required
                >
                  <option value="">Selecione...</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <fieldset className="mt-4">
              <legend className="mb-2 text-sm font-medium text-secondary-800">
                Papéis de comunicação
              </legend>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {roles
                  .filter((role) => roleTooltips[role.kind])
                  .map((role) => {
                    const selected = (form.communicationRoleIds ?? []).includes(role.id);
                    return (
                      <HoverTooltip key={role.id} text={roleTooltips[role.kind]} multiline>
                        <label className="flex items-center gap-2 text-sm text-secondary-800">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(event) => {
                              const current = form.communicationRoleIds ?? [];
                              setForm({
                                ...form,
                                communicationRoleIds: event.target.checked
                                  ? [...current, role.id]
                                  : current.filter((id) => id !== role.id),
                              });
                            }}
                          />
                          {role.name}
                        </label>
                      </HoverTooltip>
                    );
                  })}
              </div>
            </fieldset>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </FormActionBar>
        </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <select
          className="erp-input max-w-xs"
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
        >
          <option value="">Todas as empresas</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </select>
        <input
          className="erp-input max-w-md"
          placeholder="Buscar contato..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="button" className="erp-btn-secondary" onClick={() => void loadData(1)}>
          Buscar
        </button>
      </div>

      {loading ? (
        <LoadingSpinner label="Carregando contatos..." />
      ) : (
        <div className="erp-card overflow-x-auto">
          <table className="erp-table w-full">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Empresa</th>
                <th>E-mail</th>
                <th>Papéis</th>
                <th>Cargo</th>
                {canWrite ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {items.map((contact) => (
                <tr key={contact.id}>
                  <td>{contact.fullName}</td>
                  <td>{contact.companyName}</td>
                  <td>{contact.email}</td>
                  <td>
                    {(contact.communicationRoleIds ?? [])
                      .map((id) => roles.find((role) => role.id === id)?.name)
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </td>
                  <td>{contact.jobTitle ?? '—'}</td>
                  {canWrite ? (
                    <td className="text-right">
                      <EditIconButton onClick={() => startEdit(contact)} />
                      <DeleteIconButton onClick={() => void handleDelete(contact)} />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ListPaginationBar
        totalCount={totalCount}
        page={page}
        pageSize={PAGE_SIZE}
        onPrevious={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
      />
    </PageShell>
  );
}
